import axios from 'axios'
import config from '../constants/global'
import { showToaster } from './show-toaster.util'
import { captureException, addBreadcrumb } from '@sentry/react'
import { redirectToServerErrorPage } from './navigation.util'

const API = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

API.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user'))
    if (user?.token) {
      config.headers['Authorization'] = `Bearer ${user.token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

const handleError = (error) => {
  const url = error.config?.url
  console.error(`API Error on ${url}:`, error.response || error.message || error)

  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
    captureException(error)
    console.error('Network Error: Please check connection.')
    return
  }

  if (error.response && error.response.status >= 500) {
    console.error(`Server Error: ${error.response.status}`)
    showToaster('error', `Server Error (${error.response.status}). Please try again later.`)
    captureException(error)
    redirectToServerErrorPage()
    return
  }

  if (error.response && error.response.status === 404) {
    console.error('Resource not found (404)')
    showToaster('error', 'Resource not found (404).')
    addBreadcrumb({
      category: 'api',
      message: 'Not found HTTP 404 error: ' + url,
      level: 'info',
    })
    return
  }

  if (error.response && error.response.status >= 400 && error.response.status !== 401) {
    const message =
      error.response.data?.errorMessage || `Request failed (${error.response.status}).`
    console.error(`Client Error (${error.response.status}): ${message}`)
    showToaster('error', message)
    return
  }

  const message =
    error?.response?.data?.errorMessage || error.message || 'An unexpected error occurred.'
  showToaster('error', message)
  addBreadcrumb({
    category: 'api',
    message: 'Unknown API error: ' + url,
    level: 'warn',
  })
  captureException(error)
}

export const handleGetRequest = async (url, params = {}) => {
  params = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(params).filter(([_, v]) => typeof v !== 'undefined' && v !== '' && v !== null)
  )

  try {
    const response = await API.get(url, { params })
    return response.data
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/* { success: true, message: '', data: {}}  = 200 or 201 */
// { success: false, error_message: '', stacktrace: {}, errors: []} - >=400

export const handlePostRequest = async (url, data, config) => {
  try {
    const response = await API.post(url, data, config)
    return response.data
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

export const handlePutRequest = async (url, data, config) => {
  try {
    const response = await API.put(url, data, config)
    return response.data
  } catch (error) {
    handleError(error)
  }
}

export const handleDeleteRequest = async (url, config) => {
  try {
    const response = await API.delete(url, config)
    return response.data
  } catch (error) {
    handleError(error)
  }
}

export const handlePatchRequest = async (url, data, config) => {
  try {
    const response = await API.patch(url, data, config)
    return response.data
  } catch (error) {
    handleError(error)
  }
}

export const handleLogoutRequest = async () => {
  try {
    const response = await API.post('/logout')
    return response.data
  } catch (error) {
    handleError(error)
  }
}
